/** The language subtag of a locale code, e.g. "en" for both "en" and "en-GB". */
const languageSubtag = (code) => code.toLowerCase().replace('_', '-').split('-')[0];

const normalizeCode = (code) => code.toLowerCase().replace('_', '-');

/** The browser's preferred languages, most preferred first. */
const getBrowserLanguages = () => {
    if (navigator.languages && navigator.languages.length) {
        return [...navigator.languages];
    }

    return navigator.language ? [navigator.language] : [];
};

/**
 * The locale whose code matches the given code, ignoring case and
 * underscore/hyphen separator differences.
 */
export const findLocaleByCode = (locales, code) => {
    if (!code) {
        return undefined;
    }

    return locales.find((locale) => normalizeCode(locale.code) === normalizeCode(code));
};

/**
 * The locale that best matches the given code, relaxing an unmatched code to
 * its language - e.g. "?locale=fr" against a "fr-FR" Locale. For a relaxed
 * match the instance's default Locale wins for its own language, so "en"
 * resolves to a default English Locale over a regional one.
 */
export const findLocaleForLanguage = (locales, code) => {
    if (!code) {
        return undefined;
    }

    return (
        findLocaleByCode(locales, code) ??
        locales.find(
            (locale) => locale.default && languageSubtag(locale.code) === languageSubtag(code),
        ) ??
        locales.find((locale) => languageSubtag(locale.code) === languageSubtag(code))
    );
};

/**
 * The site-translation language that best matches the given Locale code. The
 * Locales are commonly more specific than the translations we ship - the
 * "fr-FR" Locale against the "fr" bundle - so an unmatched code relaxes to its
 * language, with the en-US fallback translations winning for English. If that
 * language is unavailable, English is used.
 */
export const findClosestTranslation = (code, availableLanguages) => {
    if (!code) {
        return undefined;
    }

    const exactMatch = availableLanguages.find(
        (language) => normalizeCode(language) === normalizeCode(code),
    );
    if (exactMatch) {
        return exactMatch;
    }

    const languageMatches = availableLanguages.filter(
        (language) => languageSubtag(language) === languageSubtag(code),
    );

    return (
        languageMatches.find((language) => normalizeCode(language) === 'en-us') ??
        languageMatches[0] ??
        availableLanguages.find((language) => normalizeCode(language) === 'en-us') ??
        availableLanguages.find((language) => languageSubtag(language) === 'en')
    );
};

/**
 * The locale that best matches the browser's language preferences. Each
 * preference is tried as an exact code match first, then relaxed to a language
 * match so that a browser set to "en-AU" still resolves to an English locale.
 */
const findBrowserLocale = (locales) => {
    const browserLanguages = getBrowserLanguages();

    const exactMatch = browserLanguages
        .map((language) => findLocaleByCode(locales, language))
        .find(Boolean);

    if (exactMatch) {
        return exactMatch;
    }

    return browserLanguages
        .map((language) =>
            locales.find((locale) => languageSubtag(locale.code) === languageSubtag(language)),
        )
        .find(Boolean);
};

/**
 * The locale to show when the visitor has not picked one: their browser's
 * language if the TMS supports it, otherwise the TMS default.
 */
export const resolveDefaultLocale = (locales) =>
    findBrowserLocale(locales) ?? locales.find((locale) => locale.default) ?? locales[0];
